import React, { useEffect, useState } from 'react';
import { Card, Radio, Input, Button, Tag, Space, Typography, Spin, message, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, SendOutlined, LockOutlined } from '@ant-design/icons';
import { qualificationApi, BidDecisionData } from '@/api/qualificationApi';
import { feasibilityApi, type FeasibilityReviewType } from '@/api/feasibilityApi';

const { Text } = Typography;

const REQUIRED_REVIEW_TYPES: FeasibilityReviewType[] = ['TECHNICAL', 'STAFFING', 'DELIVERY'];

const REVIEW_LABELS: Record<FeasibilityReviewType, string> = {
  TECHNICAL: 'B.1 Technical (Cloud Architect)',
  STAFFING:  'B.2 Staffing (Resourcing Manager)',
  DELIVERY:  'B.3 Delivery (BD Manager)',
};

interface BidDecisionFormProps {
  opportunityId: string;
  onComplete: () => void;
}

const BidDecisionForm: React.FC<BidDecisionFormProps> = ({ opportunityId, onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingDecision, setExistingDecision] = useState<BidDecisionData | null>(null);
  const [decision, setDecision] = useState<'GO' | 'NO_GO'>('GO');
  const [rationale, setRationale] = useState('');

  // Feasibility gate state
  const [feasibilityComplete, setFeasibilityComplete] = useState(false);
  const [pendingReviews, setPendingReviews] = useState<FeasibilityReviewType[]>(REQUIRED_REVIEW_TYPES);

  useEffect(() => {
    fetchData();
  }, [opportunityId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [decisionResult, reviewsResult] = await Promise.allSettled([
        qualificationApi.getBidDecision(opportunityId),
        feasibilityApi.getReviews(opportunityId),
      ]);

      // Handle bid decision
      if (decisionResult.status === 'fulfilled') {
        setExistingDecision(decisionResult.value);
      } else {
        setExistingDecision(null);
      }

      // Handle feasibility reviews
      if (reviewsResult.status === 'fulfilled') {
        const submittedTypes = reviewsResult.value.map((r) => r.reviewType);
        const missing = REQUIRED_REVIEW_TYPES.filter((t) => !submittedTypes.includes(t));
        setPendingReviews(missing);
        setFeasibilityComplete(missing.length === 0);
      } else {
        // Fail safe: treat as incomplete if fetch fails
        setPendingReviews(REQUIRED_REVIEW_TYPES);
        setFeasibilityComplete(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!rationale.trim()) {
      message.warning('Please provide a rationale for your decision');
      return;
    }
    setSubmitting(true);
    try {
      await qualificationApi.submitBidDecision(opportunityId, { decision, rationale });
      message.success('Bid decision submitted successfully');
      await fetchData();
      onComplete();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to submit bid decision');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card size="small" style={{ marginTop: 12 }}>
        <Spin size="small" /> Loading bid decision...
      </Card>
    );
  }

  if (existingDecision) {
    return (
      <Card size="small" style={{ marginTop: 12, background: existingDecision.decision === 'GO' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(245, 158, 11, 0.12)' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            {existingDecision.decision === 'GO' ? (
              <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 14, padding: '4px 12px' }}>
                BID GO
              </Tag>
            ) : (
              <Tag icon={<CloseCircleOutlined />} color="warning" style={{ fontSize: 14, padding: '4px 12px' }}>
                NO GO
              </Tag>
            )}
            {existingDecision.decidedBy && (
              <Text type="secondary">Decided by {existingDecision.decidedBy}</Text>
            )}
          </Space>
          {existingDecision.rationale && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Rationale:</Text>
              <br />
              <Text>{existingDecision.rationale}</Text>
            </div>
          )}
        </Space>
      </Card>
    );
  }

  return (
    <Card
      size="small"
      title={<Text strong style={{ fontSize: 14 }}>Bid/No-Bid Decision</Text>}
      style={{ marginTop: 12, borderColor: '#faad14' }}
    >
      <Alert
        message="A bid decision is required to advance to the next gate"
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
      />

      {/* Feasibility gate — locked until all 3 reviews submitted */}
      {!feasibilityComplete && (
        <Alert
          icon={<LockOutlined />}
          message="Bid decision locked — complete all feasibility reviews first"
          description={
            <div style={{ marginTop: 4 }}>
              <Text style={{ fontSize: 12 }}>Pending reviews:</Text>
              <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {pendingReviews.map((type) => (
                  <Tag key={type} color="orange" style={{ fontSize: 11 }}>
                    {REVIEW_LABELS[type]}
                  </Tag>
                ))}
              </div>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Decision:</Text>
          <Radio.Group value={decision} onChange={(e) => setDecision(e.target.value)} disabled={!feasibilityComplete}>
            <Radio.Button value="GO" style={{ minWidth: 100, textAlign: 'center' }}>
              <CheckCircleOutlined style={{ color: '#52c41a' }} /> GO
            </Radio.Button>
            <Radio.Button value="NO_GO" style={{ minWidth: 100, textAlign: 'center' }}>
              <CloseCircleOutlined style={{ color: '#faad14' }} /> NO GO
            </Radio.Button>
          </Radio.Group>
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Rationale:</Text>
          <Input.TextArea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="Explain the reasoning behind this decision..."
            rows={3}
            disabled={!feasibilityComplete}
          />
        </div>
        <div style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!rationale.trim() || !feasibilityComplete}
          >
            Submit Decision
          </Button>
        </div>
      </Space>
    </Card>
  );
};

export default BidDecisionForm;
